import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const PART_QUALITIES = [
  { id: "AFTERMARKET_STANDARD", label: "Standard Aftermarket" },
  { id: "AFTERMARKET_PREMIUM", label: "Premium Aftermarket" },
  { id: "REFURBISHED_ORIGINAL", label: "Refurbished Original" },
  { id: "GENUINE_MANUFACTURER", label: "Genuine Manufacturer" },
];

export default function ServiceConfigurator() {
  const { deviceType } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);           // /api/catalog (labels + fallback pricing)
  const [modelsTree, setModelsTree] = useState(null);     // /api/catalog/models (nested)
  const [brand, setBrand] = useState("");
  const [modelName, setModelName] = useState("");
  const [service, setService] = useState("");            // single service (per Phase 3 spec)
  const [partQuality, setPartQuality] = useState("AFTERMARKET_STANDARD");
  const [rush, setRush] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [ackEstimate, setAckEstimate] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState({ manufacturer: "", model_name: "", issue_description: "", name: "", email: "", phone: "" });
  const [submittingCustom, setSubmittingCustom] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/catalog"), api.get("/catalog/models")])
      .then(([c, m]) => { setCatalog(c.data); setModelsTree(m.data); });
  }, []);

  const deviceLabel = catalog?.device_types[deviceType]?.label || deviceType;
  const brands = useMemo(() => {
    if (!modelsTree || !modelsTree[deviceType]) return [];
    return Object.keys(modelsTree[deviceType]).sort();
  }, [modelsTree, deviceType]);

  const models = useMemo(() => {
    if (!modelsTree || !brand || !modelsTree[deviceType]?.[brand]) return [];
    return modelsTree[deviceType][brand];
  }, [modelsTree, deviceType, brand]);

  const selectedModelRow = useMemo(() => models.find((m) => m.model_name === modelName), [models, modelName]);
  const services = selectedModelRow?.services || [];

  useEffect(() => { setModelName(""); setService(""); setQuote(null); }, [brand]);
  useEffect(() => { setService(""); setQuote(null); }, [modelName]);

  useEffect(() => {
    if (!brand || !modelName || !service) { setQuote(null); return; }
    setLoadingQuote(true);
    api.post("/quote", { device_type: deviceType, brand, model_name: modelName, services: [service], rush })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null))
      .finally(() => setLoadingQuote(false));
  }, [brand, modelName, service, rush, deviceType]);

  const canContinue = brand && modelName && service && quote && ackEstimate;

  const goNext = () => {
    if (!canContinue) { toast.error("Pick brand, model, repair, and acknowledge the estimate."); return; }
    sessionStorage.setItem("repair_draft", JSON.stringify({
      device_type: deviceType, brand, model_name: modelName, services: [service], rush, quote,
      part_quality: partQuality,
      estimate_ack: { acknowledged: true, at: new Date().toISOString(),
                       estimate_snapshot: quote, service_id: service },
    }));
    navigate("/schedule");
  };

  const submitCustom = async () => {
    if (!custom.manufacturer.trim() || !custom.model_name.trim() || !custom.issue_description.trim()
        || !custom.name.trim() || !custom.email.trim() || !custom.phone.trim()) {
      toast.error("Please complete every field."); return;
    }
    setSubmittingCustom(true);
    try {
      const { data } = await api.post("/custom-quote", { device_type: deviceType, ...custom });
      toast.success(`Request received — ticket ${data.ticket_code}. We'll email a written estimate shortly.`);
      setShowCustom(false); setCustom({ manufacturer: "", model_name: "", issue_description: "", name: "", email: "", phone: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmittingCustom(false); }
  };

  if (!catalog || !modelsTree) return <div className="pt-24 text-center text-slate-500">Loading catalog…</div>;

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="overline">Step 2 of 6</div>
        <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Fixing a {deviceLabel.toLowerCase()}. Let's get specific.</h1>
        <p className="mt-2 text-slate-600">Preliminary estimates use supported models only. If your exact device isn't listed, request a custom estimate below.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Manufacturer */}
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Manufacturer / Brand</span>
            <select data-testid="brand-select-dropdown" value={brand} onChange={(e) => setBrand(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900">
              <option value="">Select a brand…</option>
              {brands.map((b) => <option key={b} value={b} data-testid={`brand-${b}`}>{b}</option>)}
            </select>
          </label>

          {/* Exact model */}
          <label className={`block ${!brand ? "opacity-40 pointer-events-none" : ""}`}>
            <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Exact model</span>
            <select data-testid="model-select" value={modelName} onChange={(e) => setModelName(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900">
              <option value="">{brand ? "Select a model…" : "Pick a brand first"}</option>
              {models.map((m) => <option key={m.model_name} value={m.model_name}>{m.model_name}</option>)}
            </select>
            {brand && (
              <button type="button" data-testid="request-custom-btn" onClick={() => setShowCustom(true)}
                className="mt-2 text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                <HelpCircle className="w-3.5 h-3.5" /> Don't see your device? Request a Custom Estimate
              </button>
            )}
          </label>

          {/* Repair type */}
          <label className={`block ${!modelName ? "opacity-40 pointer-events-none" : ""}`}>
            <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Repair type</span>
            <select data-testid="service-select" value={service} onChange={(e) => setService(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900">
              <option value="">{modelName ? "Select a repair…" : "Pick a model first"}</option>
              {services.map((s) => (
                <option key={s} value={s} data-testid={`service-${s}`}>{catalog.services[s]?.label || s}</option>
              ))}
            </select>
          </label>

          {/* Part quality */}
          <label className={`block ${!service ? "opacity-40 pointer-events-none" : ""}`}>
            <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Preferred part quality</span>
            <select data-testid="part-quality-select" value={partQuality} onChange={(e) => setPartQuality(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900">
              {PART_QUALITIES.map((q) => <option key={q.id} value={q.id}>{q.label}</option>)}
            </select>
            <p className="mt-1 text-[11px] text-slate-500">Actual part on your device is confirmed at in-person inspection. Only parts documented as genuine manufacturer will be described that way.</p>
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="rush-toggle" checked={rush} onChange={(e) => setRush(e.target.checked)} className="w-4 h-4 rounded border-slate-300" />
            Rush repair (+$35 labor)
          </label>
        </div>

        {/* Estimate sidebar */}
        <div className="lg:col-span-1">
          <div data-testid="bid-summary-container" className="sticky top-24 p-6 md:p-8 rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overline mb-1" data-testid="estimate-heading">Preliminary Online Estimate</div>
            <div className="text-[10px] text-slate-500 mb-3">Instant Repair Estimate — not final authorization</div>
            {loadingQuote && <p className="text-sm text-slate-400">Calculating…</p>}
            {!loadingQuote && !quote && <p className="text-sm text-slate-500">Pick a brand, model and repair to see your estimate.</p>}
            {quote && (
              <>
                <div className="text-xs text-slate-500 mb-1">Device</div>
                <div className="text-sm text-slate-900 mb-2">{brand} {modelName}</div>
                <div className="text-xs text-slate-500 mb-1">Repair</div>
                <div className="text-sm text-slate-900 mb-2">{catalog.services[service]?.label}</div>
                <div className="text-xs text-slate-500 mb-1">Part quality</div>
                <div className="text-sm text-slate-900 mb-4">{PART_QUALITIES.find((q) => q.id === partQuality)?.label}</div>

                <div className="border-t border-slate-100 pt-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span>Replacement Part</span><span data-testid="parts-cost-amount" className="font-mono">${quote.parts_total.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>Repair Labor</span><span data-testid="labor-cost-amount" className="font-mono">${quote.labor_total.toFixed(2)}</span></div>
                  <div className="flex justify-between text-slate-500"><span>Estimated Sales Tax</span><span data-testid="tax-amount" className="font-mono">${quote.tax_total.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold pt-2 border-t border-slate-100 text-slate-900">
                    <span>Estimated Total</span><span data-testid="total-quote-amount" className="font-mono">${quote.grand_total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-4 space-y-1 text-xs">
                  <div className="flex justify-between px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900">
                    <span>PARTS PAYMENT DUE ONLINE</span><span className="font-mono font-bold">${quote.parts_total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between px-3 py-2 rounded-lg bg-sky-50 border border-sky-200 text-sky-900">
                    <span>LABOR DUE AT PICKUP</span><span className="font-mono font-bold">${quote.labor_total.toFixed(2)}</span>
                  </div>
                </div>

                <label className="mt-5 flex items-start gap-2 text-xs text-slate-700">
                  <input type="checkbox" data-testid="ack-estimate" checked={ackEstimate} onChange={(e) => setAckEstimate(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-slate-300" />
                  <span>I understand that this is a preliminary online estimate and is not final authorization for The Repair Nook to perform repair work.</span>
                </label>
              </>
            )}

            <button data-testid="accept-schedule-btn" onClick={goNext} disabled={!canContinue || loadingQuote}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition disabled:opacity-40 disabled:cursor-not-allowed">
              Continue &amp; Schedule Drop-Off <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-3 text-[11px] leading-snug text-slate-500">This preliminary estimate is based on the device and repair information you provided. Final pricing is subject to physical inspection of your device. No repair work will begin until The Repair Nook provides a written repair estimate and receives your authorization to proceed. If additional repairs or charges become necessary, we will request your approval before performing that additional work.</p>
          </div>
        </div>
      </div>

      {/* Custom quote modal */}
      {showCustom && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCustom(false)}>
          <div className="max-w-lg w-full bg-white rounded-2xl border border-slate-200 shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-slate-900">Request a Custom Estimate</h2>
            <p className="text-sm text-slate-600 mt-1">We'll email a written estimate for models we don't carry pricing on.</p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              {[["manufacturer", "Manufacturer"], ["model_name", "Model"], ["name", "Your name"], ["email", "Email"], ["phone", "Phone"]].map(([k, l]) => (
                <label key={k} className="block text-sm col-span-2 sm:col-span-1">
                  <span className="text-xs text-slate-500 uppercase">{l}</span>
                  <input data-testid={`custom-${k}`} value={custom[k]} onChange={(e) => setCustom({ ...custom, [k]: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
                </label>
              ))}
              <label className="block text-sm col-span-2">
                <span className="text-xs text-slate-500 uppercase">Describe the issue</span>
                <textarea data-testid="custom-issue" rows={3} value={custom.issue_description} onChange={(e) => setCustom({ ...custom, issue_description: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
              </label>
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShowCustom(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm">Cancel</button>
              <button data-testid="custom-submit" onClick={submitCustom} disabled={submittingCustom} className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40">
                {submittingCustom ? "Submitting…" : "Request custom estimate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
